import { useEffect, useRef, useState } from 'react';
import constants from '../../config/constants';
import { CookieStorage } from '../../utils/CookieStorage';
import _ from '../../utils/LodashImports';
import { getContextIdFromCookie, getWidgetsApiParams, groupByBackendCategoryId } from '../../utils/RecoHelper';
import { usePriceMapper } from '../../utils/hooks/usePriceMapper';
import { usePriceMapperV2 } from '../../utils/hooks/usePriceMapperV2';
import { carouselActions, recoAnalytics } from '../../utils/recoAnalytics';
import getLabels from './ShopTheProject.labels';
import { excludeMainProduct, mapClickMetrics } from './ShopTheProject.utils';

const localStorageKey = 'shop-the-project-already-seen-by-user';
const slotsComplementaryProducts = 'PDP-PRJ-1';
const slotsSimilarProducts = 'PDP-PRJ-2';
const minComplementaryProducts = 3;
const maxComplementaryProducts = 5;
const maxSimilarProducts = 5;

const storeCodes = {
  [constants.STORES.sodimac]: 'SO',
  [constants.STORES.tottus]: 'TO',
  [constants.STORES.linio]: 'LI',
  [constants.STORES.tienda]: 'TI',
  [constants.STORES.homecenter]: 'SO',
  [constants.STORES.so_com]: 'SO',
  [constants.STORES.so_retail]: 'SO',
  [constants.STORES.to_com]: 'TO',
};

export const useShopTheProject = ({ activeImageIndex, mainProductImage, appCtx, product, cart }) => {
  const projectNamePrimary = useRef(undefined);
  const projectNameSecondary = useRef(undefined);
  const storeCode = storeCodes[appCtx.store];
  const storeAndCountryCode = `${storeCode}${appCtx.regionCode.toUpperCase()}`;
  const [showCoachmark, setShowCoachmark] = useState(undefined);
  const [initialPrimaryProducts, setInitialPrimaryProducts] = useState([]);
  const { mapPrices } = usePriceMapper();
  const { mapPricesV2 } = usePriceMapperV2();
  const labels = getLabels(appCtx);

  const fetchProducts = async ({
    productId,
    variantId,
    slots,
    categoryId,
    webCategoryId,
    brandName,
    sellerId,
    type,
  }) => {
    const contextId = getContextIdFromCookie(appCtx);
    const widgetsUUID = '';
    const userId = CookieStorage.getCookie('USER_ID') || '';
    const response = await product.loadRecommendedProducts(
      getWidgetsApiParams({
        appCtx,
        product: {
          id: productId,
          currentVariant: variantId,
          attributes: {
            GSCCategoryId: categoryId,
          },
          defaultParentCategoryId: webCategoryId,
          brandName,
          sellerInfo: { sellerId },
        },
        slots,
        widgetsUUID,
        userId,
        contextId,
      })
    );
    const name = _.get(response, '[0].name', undefined);
    const rawProducts = _.get(response, '[0].data', []);
    const products = rawProducts.map((fetchedProduct) => ({
      ...fetchedProduct,
      variantId: fetchedProduct.offeringId,
      price: mapPrices(fetchedProduct),
      type,
      driverVariantId: variantId,
    }));
    return { name, products };
  };

  const mainProduct = {
    productId: product.id,
    variantId: product.currentVariant,
    displayName: product.name,
    brand: product.brandName,
    price: mapPricesV2(_.get(product, 'variants[0]', {})),
    url: window.location.href,
    sellerName: product.sellerInfo.sellerName,
    mediaUrls: [mainProductImage],
  };

  useEffect(() => {
    (async () => {
      const isShopTheProjectCoachmarkEnabled = _.get(
        appCtx,
        'siteConfig.toggles.isShopTheProjectCoachmarkEnabled',
        false
      );
      const isFirstRenderForThisUser = window?.localStorage?.getItem(localStorageKey) === null;
      setShowCoachmark(isShopTheProjectCoachmarkEnabled && isFirstRenderForThisUser);
      if (isFirstRenderForThisUser) window?.localStorage?.setItem(localStorageKey, true);
      const { name, products } = await fetchProducts({
        productId: product.id,
        variantId: product.currentVariant,
        slots: slotsComplementaryProducts,
        categoryId: _.get(product, 'attributes.GSCCategoryId', ''),
        webCategoryId: product.defaultParentCategoryId,
        brandName: product.brandName,
        sellerId: _.get(product, 'variants[0].offerings[0].sellerId', ''),
        type: 'complement',
      });
      const primaryProducts = groupByBackendCategoryId({
        products,
        filterOptions: {
          maxGroups: maxComplementaryProducts,
          podsByGroup: 1,
          minPods: minComplementaryProducts,
        },
      });
      projectNamePrimary.current = name;
      setInitialPrimaryProducts(primaryProducts);
      if (primaryProducts.length) {
        recoAnalytics.impression({
          carouselName: `STP_Widget_${name}`,
          skusViewed: [product.currentVariant],
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddToCart = async (productsToAddToCart) => {
    const productsData = productsToAddToCart.map((productToAddToCart) => ({
      skuId: productToAddToCart.variantId,
      productId: productToAddToCart.productId,
      quantity: productToAddToCart.quantity,
      offeringId: productToAddToCart.variantId,
    }));
    return cart.addProduct({
      appCtx,
      products: productsData,
      product,
    });
  };

  const handleImpression = ({ driverProduct, visibleVariant, carouselName }) => {
    const { variantId: driverVariantId } = driverProduct;
    const { variantId: visibleVariantId } = visibleVariant;
    if (driverVariantId === visibleVariantId) {
      return;
    }
    recoAnalytics.impression({
      carouselName: `STP_${carouselName}`,
      skusViewed: [visibleVariantId],
      driverVariantId,
    });
  };

  const getSecondaryProducts = async (primaryProduct) => {
    const { name, products } = await fetchProducts({
      productId: primaryProduct.productId,
      variantId: primaryProduct.variantId,
      slots: slotsSimilarProducts,
      categoryId: '',
      webCategoryId: '',
      brandName: primaryProduct.brand,
      sellerId: primaryProduct.sellerName,
      type: 'similar',
    });
    projectNameSecondary.current = name;
    return products.slice(0, maxSimilarProducts).map((secondaryProduct) => ({
      ...secondaryProduct,
      variantId: secondaryProduct.offeringId,
    }));
  };

  const onClickOnCallToAction = (primaryProducts) => {
    recoAnalytics.click({
      carouselKey: 'cr_recommendationstp',
      siteName: storeAndCountryCode,
      carouselName: projectNamePrimary.current,
      pageName: mainProduct.variantId,
      podPositionInCarousel: activeImageIndex,
      lengthPodsInCarousel: excludeMainProduct(primaryProducts).length,
      slotId: slotsComplementaryProducts,
    });
  };

  const onPrimaryProductImpression = (driverProduct, complementaryProduct) => {
    handleImpression({
      driverProduct,
      visibleVariant: complementaryProduct,
      carouselName: projectNamePrimary.current,
    });
  };

  const onSecondaryProductImpression = (complementaryProduct, similaryProduct) => {
    handleImpression({
      driverProduct: complementaryProduct,
      visibleVariant: similaryProduct,
      carouselName: projectNameSecondary.current,
    });
  };

  const onAddToCart = (primaryProductsOnCart, allPrimaryProducts) => {
    const { selectedVariantsIds, podPositions, podsInCarousel } = mapClickMetrics(
      primaryProductsOnCart,
      allPrimaryProducts
    );
    recoAnalytics.click({
      carouselKey: 'cr_recommendationstp',
      siteName: storeAndCountryCode,
      action: carouselActions.addToCart,
      carouselName: projectNamePrimary.current,
      pageName: mainProduct.variantId,
      skuId: selectedVariantsIds,
      podPositionInCarousel: podPositions,
      lengthPodsInCarousel: podsInCarousel.length,
      slotId: slotsComplementaryProducts,
    });
  };

  const onClickOnProductName = (clickedProduct, allProducts) => {
    const configByProject = {
      complement: {
        carouselName: projectNamePrimary.current,
        slotId: slotsComplementaryProducts,
      },
      similar: {
        carouselName: projectNameSecondary.current,
        slotId: slotsSimilarProducts,
      },
    };
    const projectConfig = configByProject[clickedProduct.type] || configByProject.complement;
    const { podPositions: productPosition, podsInCarousel } = mapClickMetrics([clickedProduct], allProducts);
    recoAnalytics.click({
      carouselKey: 'cr_recommendationstp',
      siteName: storeAndCountryCode,
      action: carouselActions.clickToRate,
      carouselName: projectConfig.carouselName,
      pageName: clickedProduct.driverVariantId,
      skuId: clickedProduct.variantId,
      podPositionInCarousel: productPosition,
      lengthPodsInCarousel: podsInCarousel.length,
      slotId: projectConfig.slotId,
    });
  };

  const onClickOnExchange = (primaryProduct, secondaryProduct, _allPrimaryProduct, allSecondaryProduct) => {
    const productPosition = allSecondaryProduct.findIndex(
      (productInArray) => productInArray.variantId === secondaryProduct.variantId
    );
    recoAnalytics.click({
      carouselKey: 'cr_recommendationstp',
      siteName: storeAndCountryCode,
      carouselName: projectNameSecondary.current,
      pageName: primaryProduct.variantId,
      skuId: secondaryProduct.variantId,
      podPositionInCarousel: productPosition + 1,
      lengthPodsInCarousel: allSecondaryProduct.length,
      slotId: slotsSimilarProducts,
    });
  };

  const analytics = {
    onClickOnCallToAction,
    onPrimaryProductImpression,
    onSecondaryProductImpression,
    onAddToCart,
    onClickOnProductName,
    onClickOnExchange,
  };

  return {
    initialPrimaryProducts,
    minComplementaryProducts,
    labels,
    showCoachmark,
    maxComplementaryProducts,
    mainProduct,
    handleAddToCart,
    getSecondaryProducts,
    analytics,
  };
};
